// Firestore data layer for the assessment app.
//
// All result/coach-code/user persistence goes through this module so the UI
// components stay thin and there is a single place that talks to Firestore.
// The psychometric engine and item bank remain entirely client-side and do not
// touch this module.
//
// Collections (in the shared APC Firebase project):
//   assessmentResults/{resultId}  ownerUid, email, coachId?, clientId?, tier, resultsData, createdAt
//   assessmentCodes/{code}        coachId, clientId?, status, createdAt, expiresAt, usedAt?, usedByUid?
//   assessmentUsers/{uid}         email, displayName, tier, lastPaymentIntentId, createdAt
//   coaches/{uid}                 (existing CRM coach identity, read-only here)
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { activeIdentity, launchAttribution, progressFitsLaunch, tagProgress } from './identity';

// S5b.3: the launch identity's Firestore and Functions while a launch holds this tab, else the default's.
const dbNow = () => activeIdentity().db;
const functionsNow = () => activeIdentity().functions;

const RESULTS = 'assessmentResults';
const CODES = 'assessmentCodes';
const COACHES = 'coaches';
const SHARES = 'assessmentShares';
const USERS = 'assessmentUsers';

function newSessionId() {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// Unguessable share token used as the public doc id. Combines time, randomness
// and (when available) crypto random so it cannot be enumerated or guessed.
function newShareId() {
  const rand = () => Math.random().toString(36).slice(2, 12);
  let entropy = rand() + rand();
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      entropy = crypto.randomUUID().replace(/-/g, '');
    }
  } catch (_) { /* fall back to Math.random entropy */ }
  return `sh_${Date.now().toString(36)}${entropy}`;
}

function sortByCreatedDesc(docs) {
  return docs.sort((a, b) => {
    const at = a.createdAtMs || 0;
    const bt = b.createdAtMs || 0;
    return bt - at;
  });
}

function normalizeResult(snap) {
  const data = snap.data();
  const created = data.createdAt;
  return {
    id: snap.id,
    ...data,
    createdAtMs: created && created.toMillis ? created.toMillis() : 0,
  };
}

// -------------------- Coach codes --------------------

// Generate a human-friendly code like APC-7Q4M-3KXA.
function generateCodeString() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const block = (n) =>
    Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `APC-${block(4)}-${block(4)}`;
}

// Coach-side: create one or more codes tied to a coach (and optionally a client).
// Used by the in-app coach dashboard and mirrored by the CRM client view.
// Coach codes grant a free tier to the client (default PREMIUM, the full
// constellation). grantsTier is stored on the code and applied at registration.
export async function generateCoachCodes({
  coachId,
  clientId = null,
  count = 1,
  expiresInDays = 90,
  grantsTier = 'PREMIUM',
}) {
  if (!coachId) throw new Error('coachId is required to generate codes');
  const created = [];
  for (let i = 0; i < Math.min(Math.max(count, 1), 50); i += 1) {
    let code = generateCodeString();
    // Avoid the (very unlikely) collision on the code doc id.
    // eslint-disable-next-line no-await-in-loop
    while ((await getDoc(doc(dbNow(), CODES, code))).exists()) {
      code = generateCodeString();
    }
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + expiresInDays);
    // eslint-disable-next-line no-await-in-loop
    await setDoc(doc(dbNow(), CODES, code), {
      code,
      coachId,
      clientId,
      status: 'active',
      grantsTier: (grantsTier || 'PREMIUM').toUpperCase(),
      createdAt: serverTimestamp(),
      expiresAt,
    });
    created.push(code);
  }
  return created;
}

export async function listCoachCodes(coachId) {
  const q = query(collection(dbNow(), CODES), where('coachId', '==', coachId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function deleteCoachCode(code) {
  await deleteDoc(doc(dbNow(), CODES, code));
}

// Built-in community / founding-tester codes. These grant PREMIUM without any
// Firestore doc, so no admin seed is needed (launch workstream 0.4). A real
// assessmentCodes/{CODE} doc, if one exists, ALWAYS takes precedence (that path
// supports a live redemption cap + instant kill switch); this map is the
// zero-infra default. Expiry is baked here, so to change expiry or retire a code
// edit this map and redeploy.
const BUILTIN_COMMUNITY_CODES = {
  FOUNDINGSTARS: {
    grantsTier: 'PREMIUM',
    cohort: 'beta-wave-1',
    source: 'mailing-list',
    expiresAt: '2026-09-21T00:00:00Z', // ~90 days from 2026-06-23 launch
  },
};

// The shared founding-tester code used for referral invites (launch workstream
// 3.2, lightweight path). Anyone arriving with this code gets the full Complete
// Universe free, so a shared result doubles as a free pass for the friend who
// opens it. Same expiry/retire story as the built-in map above. Swap if the
// referral cohort ever needs its own code.
export const REFERRAL_INVITE_CODE = 'FOUNDINGSTARS';
export const REFERRAL_INVITE_PATH = `/?code=${REFERRAL_INVITE_CODE}&tier=PREMIUM`;

// Public: validate a code before the assessment. Returns coach info for display.
export async function validateCoachCode(code) {
  if (!code) return { valid: false };
  const normalized = code.trim().toUpperCase();
  const snap = await getDoc(doc(dbNow(), CODES, normalized));
  if (!snap.exists()) {
    // No Firestore doc: fall back to a built-in community code if one matches.
    const builtin = BUILTIN_COMMUNITY_CODES[normalized];
    if (builtin) {
      if (builtin.expiresAt && new Date(builtin.expiresAt).getTime() < Date.now()) {
        return { valid: false, reason: 'expired' };
      }
      return {
        valid: true,
        code: normalized,
        type: 'community',
        coachId: null,
        clientId: null,
        grantsTier: (builtin.grantsTier || 'PREMIUM').toUpperCase(),
        cohort: builtin.cohort || null,
        source: builtin.source || null,
        coachInfo: null,
      };
    }
    return { valid: false, reason: 'not_found' };
  }
  const data = snap.data();
  const isExpired = !!(data.expiresAt && data.expiresAt.toMillis && data.expiresAt.toMillis() < Date.now());

  // Community / beta codes are SHARED and multi-use (one founding-tester code goes
  // to a whole wave), so they are not single-use like coach codes: an 'active'
  // status plus not-expired is the whole gate. The redemption cap is enforced out
  // of band (seeded maxRedemptions + a manual kill switch that flips status away
  // from 'active'), because the Firestore rules only let a taker flip a code to
  // 'used', not increment a live counter. See launch workstream 0.4. No coachId,
  // so no coach lookup and no coach-attribution UI.
  if (data.type === 'community') {
    if (data.status && data.status !== 'active') return { valid: false, reason: 'used' };
    if (isExpired) return { valid: false, reason: 'expired' };
    return {
      valid: true,
      code: snap.id,
      type: 'community',
      coachId: null,
      clientId: null,
      grantsTier: (data.grantsTier || 'PREMIUM').toUpperCase(),
      cohort: data.cohort || null,
      source: data.source || null,
      coachInfo: null,
    };
  }

  // Coach codes: single-use, attributed to a coach.
  if (data.status !== 'active') return { valid: false, reason: 'used' };
  if (isExpired) return { valid: false, reason: 'expired' };
  let coachInfo = null;
  try {
    const coachSnap = await getDoc(doc(dbNow(), COACHES, data.coachId));
    if (coachSnap.exists()) {
      const c = coachSnap.data();
      coachInfo = {
        id: data.coachId,
        firstName: c.firstName,
        lastName: c.lastName,
        practiceName: c.practiceName || c.bio || null,
      };
    }
  } catch (err) {
    console.warn('Could not load coach info for code:', err);
  }
  return {
    valid: true,
    code: snap.id,
    type: 'coach',
    coachId: data.coachId,
    clientId: data.clientId || null,
    grantsTier: (data.grantsTier || 'PREMIUM').toUpperCase(),
    coachInfo,
  };
}

// S2b.5 (2026-10-10): what a code means at SAVE time, for all three
// assessments. A community code attributes the result to its cohort, as
// before. Any other code is a legacy coach code: the result is saved with
// whatever the local check found (nothing, once a code is used), and then
// `recordLegacyCodeSave` hands the code to the server, which reads the code
// itself whatever its status, marks it used, stamps the result with its client,
// coach and grant, and completes the grant. So this check no longer decides
// whether a result reaches its client; it only gates what it gated before
// (a typed code's Premium access, in CosmicCoachCodeInput).
export async function attributionForCode(coachCode) {
  const out = { coachId: null, clientId: null, communityCode: null, cohort: null, source: null, legacyCode: null };
  if (!coachCode) return out;
  const normalized = coachCode.trim().toUpperCase();
  try {
    const v = await validateCoachCode(normalized);
    if (v.valid && v.type === 'community') {
      out.communityCode = v.code;
      out.cohort = v.cohort || null;
      out.source = v.source || null;
      return out;
    }
    if (v.valid) {
      out.coachId = v.coachId || null;
      out.clientId = v.clientId || null;
    } else if (v.reason === 'not_found') {
      return out;
    }
  } catch (err) {
    // Attribution must NEVER block the core result write; the server still resolves the code.
    console.warn('Coach-code check failed; the server will resolve the code:', err?.message || err);
  }
  out.legacyCode = normalized;
  return out;
}

// After the result is saved: the server marks the code used and attaches the
// result to the code's client. Tries twice, never throws (the result is
// already saved), and leaves a durable marker when both tries fail so the
// result can be attached later.
export async function recordLegacyCodeSave({ code, assessmentKey, resultId }) {
  const call = httpsCallable(functionsNow(), 'completeLegacyAssessment');
  let lastErr = null;
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await call({ code, assessmentKey, resultId });
      return { ok: true, ...((res && res.data) || {}) };
    } catch (err) {
      lastErr = err;
      console.warn(`Could not record the code for this result (try ${attempt}/2):`, err?.message || err);
    }
  }
  try {
    localStorage.setItem('apcCodeRecordFailed', JSON.stringify({
      code, assessmentKey, resultId, at: new Date().toISOString(), error: String(lastErr?.message || lastErr),
    }));
  } catch (_) { /* localStorage optional */ }
  console.error('The result is saved, but it could not be linked to the coach code.', lastErr);
  return { ok: false, error: String(lastErr?.message || lastErr) };
}

// The shared tail of every save: hand a legacy code to the server once the result exists.
/**
 * S5b.3: who a result belongs to. Under a launch, the launch says (its claim,
 * checked against the tab's state, see identity.js) and any ?code= is ignored;
 * otherwise today's coach-code attribution.
 */
export async function attributionFor(coachCode) {
  const launched = await launchAttribution();
  return launched || attributionForCode(coachCode);
}

/** The grant fields a launched result carries; nothing on today's path. */
export function grantFieldsOf(attribution) {
  return attribution && attribution.grantId ? { grantId: attribution.grantId, attempt: attribution.attempt } : {};
}

// S5b.3: a launched result completes its grant on the server, which checks the
// caller's launch claim against the grant. Tries twice, never throws.
async function completeLaunchedGrant({ grantId, resultId }) {
  const call = httpsCallable(functionsNow(), 'completeAssessmentGrant');
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await call({ grantId, resultId });
      return true;
    } catch (err) {
      console.warn(`Could not complete the grant for this result (try ${attempt}/2):`, err?.message || err);
    }
  }
  return false;
}

export async function afterResultSaved({ attribution, ownerUid, assessmentKey, resultId }) {
  if (attribution.grantId) {
    const completed = await completeLaunchedGrant({ grantId: attribution.grantId, resultId });
    return { clientId: attribution.clientId, codeRecorded: null, grantCompleted: completed };
  }
  if (!attribution.legacyCode || !ownerUid) return { clientId: attribution.clientId, codeRecorded: null };
  const recorded = await recordLegacyCodeSave({ code: attribution.legacyCode, assessmentKey, resultId });
  return { clientId: (recorded.ok && recorded.clientId) || attribution.clientId, codeRecorded: recorded.ok };
}

// -------------------- Results --------------------

// Firestore rejects `undefined` field values ANYWHERE in a document (it throws
// "Unsupported field value: undefined"). Engine result objects routinely contain
// undefined leaves (e.g. careerGuidance.primaryDomain when a domain wasn't
// resolved), so deep-strip undefined before writing. Plain objects are recursed;
// non-plain objects (the serverTimestamp() sentinel, Date, etc.) are left intact;
// undefined array elements are dropped.
function stripUndefined(value) {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.map(stripUndefined).filter((v) => v !== undefined);
  }
  if (value && typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value; // sentinel/Date/class instance
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const cleaned = stripUndefined(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }
  return value;
}

// Firestore caps a document at ~1 MiB. As defensive hygiene, drop known-heavy
// engine internals before writing a result to Firestore, so a result doc can
// never balloon (raw responses, session state, per-element item banks). This is
// a conservative BLACKLIST: it removes only internal keys the results view never
// reads, so nothing rendered is lost. The full object still lives in localStorage
// for the live UX. Not the root cause of the 2026-08 save failure (the Premium
// result object was already compact), but cheap insurance.
const HEAVY_RESULT_KEYS = [
  'responses', 'session', 'items', 'item', 'element_estimates',
  'candidate_elements', 'sessionState', 'engineSession', 'rawResponses',
];
function stripItemMetadata(arr) {
  if (!Array.isArray(arr)) return arr;
  return arr.map((el) => {
    if (el && typeof el === 'object' && ('item' in el || 'items' in el)) {
      const clone = { ...el };
      delete clone.item;
      delete clone.items;
      return clone;
    }
    return el;
  });
}
export function slimResults(results) {
  if (!results || typeof results !== 'object') return results;
  const out = { ...results };
  HEAVY_RESULT_KEYS.forEach((k) => { delete out[k]; });
  if (out.allElements) out.allElements = stripItemMetadata(out.allElements);
  if (out.topElements) out.topElements = stripItemMetadata(out.topElements);
  if (out.topPreferences) out.topPreferences = stripItemMetadata(out.topPreferences);
  return out;
}

// Durability net: mirror the finished result onto the signed-in user's own
// assessmentUsers/{uid} doc (the SAME owner-write path the resume feature uses,
// which is proven to work). If the primary assessmentResults write ever fails,
// the result still exists server-side under the user and can be reconciled,
// instead of living only in their browser. Owner-writable, tier untouched, so no
// rules change. Best-effort: never throws.
export async function saveResultToProfile({ ownerUid, results, tier = 'FREE', coachCode = null }) {
  if (!ownerUid || !results) return;
  try {
    const ref = doc(dbNow(), USERS, ownerUid);
    const lastResult = {
      results: stripUndefined(slimResults(results)),
      tier,
      coachCode: coachCode || null,
      updatedAtMs: Date.now(),
    };
    await setDoc(ref, { lastResult, updatedAt: serverTimestamp() }, { merge: true });
  } catch (err) {
    console.warn('saveResultToProfile failed (non-fatal):', err?.message || err);
  }
}

// Persist a completed assessment. A coach code attributes it: see
// attributionForCode and recordLegacyCodeSave (S2b.5).
export async function saveAssessmentResults({
  ownerUid = null,
  email,
  firstName = '',
  lastName = '',
  results,
  tier = 'FREE',
  sessionId = newSessionId(),
  coachCode = null,
  assessmentType = 'cosmic',
}) {
  const attribution = await attributionFor(coachCode);
  const { coachId, clientId, communityCode, cohort, source } = attribution;

  const payload = {
    ownerUid,
    email: email ? email.toLowerCase() : null,
    firstName,
    lastName,
    coachId,
    clientId,
    communityCode,
    ...grantFieldsOf(attribution),
    cohort,
    source,
    tier,
    assessmentType,
    sessionId,
    resultsData: slimResults(results),
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(dbNow(), RESULTS), stripUndefined(payload));

  // Never throws: the result is already saved.
  const after = await afterResultSaved({ attribution, ownerUid, assessmentKey: 'constellation', resultId: ref.id });

  return { id: ref.id, sessionId, coachId, clientId: after.clientId, codeRecorded: after.codeRecorded };
}

// Retrieve the most recent result for a signed-in user.
export async function getResultsForUser(ownerUid) {
  if (!ownerUid) return null;
  const q = query(collection(dbNow(), RESULTS), where('ownerUid', '==', ownerUid));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return sortByCreatedDesc(snap.docs.map(normalizeResult))[0];
}

// Retrieve the most recent result for an email (guest fallback).
export async function getResultsByEmail(email) {
  if (!email) return null;
  const q = query(collection(dbNow(), RESULTS), where('email', '==', email.toLowerCase()));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return sortByCreatedDesc(snap.docs.map(normalizeResult))[0];
}

// -------------------- In-progress assessment (resume) --------------------
//
// Account-linked resume: the psychometric engine already saves its session to
// localStorage (device-bound) after every answer. We mirror that blob onto the
// signed-in user's own assessmentUsers/{uid} doc under `inProgress`, so a taker
// who signs in on another device (or clears their browser) can pick up where
// they left off. The existing assessmentUsers rules already allow the owner to
// write their own doc (tier must be unchanged), so this needs NO Firestore rules
// change. Stored blob is the taker's own answers only; no extra PII.

// Upsert the in-progress session blob for a signed-in user. `state` is the
// engine's `cosmicAssessmentState` object exactly as it lives in localStorage.
export async function saveAssessmentProgress({ ownerUid, email, state }) {
  if (!ownerUid || !state) return;
  const ref = doc(dbNow(), USERS, ownerUid);
  const inProgress = tagProgress({ state, updatedAtMs: Date.now() });
  const snap = await getDoc(ref);
  if (snap.exists()) {
    // Update: leave `tier` untouched so the owner-update rule passes.
    await setDoc(ref, { inProgress, updatedAt: serverTimestamp() }, { merge: true });
  } else {
    // Create: the rules require tier === 'FREE' on first write.
    await setDoc(ref, {
      email: email ? email.toLowerCase() : null,
      tier: 'FREE',
      inProgress,
      createdAt: serverTimestamp(),
    });
  }
}

// Read the in-progress session for a signed-in user, or null if none. Returns
// { state, updatedAtMs } where state is the localStorage blob to rehydrate.
export async function getAssessmentProgress(ownerUid) {
  if (!ownerUid) return null;
  const snap = await getDoc(doc(dbNow(), USERS, ownerUid));
  if (!snap.exists()) return null;
  const p = snap.data().inProgress || null;
  return p && progressFitsLaunch(p) ? p : null;
}

// Clear the in-progress session (called once the assessment completes).
export async function clearAssessmentProgress(ownerUid) {
  if (!ownerUid) return;
  const ref = doc(dbNow(), USERS, ownerUid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  await setDoc(ref, { inProgress: null, updatedAt: serverTimestamp() }, { merge: true });
}

// -------------------- Coach dashboard --------------------

// Clients of a coach are the result records carrying their coachId.
export async function getCoachClients(coachId) {
  if (!coachId) return [];
  const q = query(collection(dbNow(), RESULTS), where('coachId', '==', coachId));
  const snap = await getDocs(q);
  return sortByCreatedDesc(snap.docs.map(normalizeResult));
}

// Lightweight analytics derived from a coach's results + codes.
export async function getCoachAnalytics(coachId) {
  const [clients, codes] = await Promise.all([getCoachClients(coachId), listCoachCodes(coachId)]);
  const tierCounts = clients.reduce((acc, c) => {
    const t = (c.tier || 'FREE').toUpperCase();
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  return {
    totalClients: clients.length,
    totalCodes: codes.length,
    activeCodes: codes.filter((c) => c.status === 'active').length,
    usedCodes: codes.filter((c) => c.status === 'used').length,
    tierCounts,
  };
}

// Read one result document by its id. Used when a coach opens a specific
// client's result (e.g. via /results?resultId=...). APC staff can read any
// result per Firestore rules; the owner can read their own.
export async function getResultById(resultId) {
  if (!resultId) return null;
  const snap = await getDoc(doc(dbNow(), RESULTS, resultId));
  if (!snap.exists()) return null;
  return normalizeResult(snap);
}

// Read a single result by clientId (used by the CRM client view bridge).
export async function getResultsForClient(clientId) {
  if (!clientId) return null;
  const q = query(collection(dbNow(), RESULTS), where('clientId', '==', clientId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return sortByCreatedDesc(snap.docs.map(normalizeResult))[0];
}

// -------------------- Public shares --------------------
//
// A share is a PUBLIC, read-only snapshot of a result that the taker chose to
// share via an unguessable link (collection: assessmentShares). It deliberately
// holds ONLY what is needed to render the read-only constellation — the score
// data, an optional first name, and the tier. It carries NO email, NO coach
// attribution and NO contact info, so the private assessmentResults doc stays
// fully locked down. Firestore rules allow public `get`, owner-only writes.

// Create a public share snapshot for the signed-in owner. Returns the shareId
// (the unguessable token used in the public URL). The caller is responsible for
// being authenticated as `ownerUid` (Firestore rules enforce it).
export async function createAssessmentShare({ ownerUid, firstName = '', results, tier = 'FREE', shareId = null }) {
  if (!ownerUid) throw new Error('ownerUid is required to create a share');
  if (!results) throw new Error('results are required to create a share');
  // Reuse the caller's existing token when given (keeps the public link stable
  // while refreshing it with the latest results); otherwise mint a new one.
  const id = shareId || newShareId();
  const payload = {
    ownerUid,
    firstName: (firstName || '').trim(),
    tier: (tier || 'FREE').toUpperCase(),
    assessmentType: 'cosmic',
    // resultsData is the processed score object the cosmic view renders. It is
    // motivational scores only, no PII, which is why it is safe to expose. Slim
    // it so the share doc can never balloon past Firestore's 1 MiB cap.
    resultsData: slimResults(results),
    createdAt: serverTimestamp(),
  };
  await setDoc(doc(dbNow(), SHARES, id), stripUndefined(payload));
  return id;
}

// Public read of a share snapshot by its token. No auth required.
export async function getAssessmentShare(shareId) {
  if (!shareId) return null;
  const snap = await getDoc(doc(dbNow(), SHARES, shareId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    firstName: data.firstName || '',
    // Normalize to lowercase to match the USER_TIERS constants ('free' | 'enhanced'
    // | 'premium'). Uppercasing here made every Premium/Enhanced share fall back to
    // the Free (top-3) view, because the star grid compares tier === USER_TIERS.PREMIUM.
    tier: (data.tier || 'free').toLowerCase(),
    resultsData: data.resultsData || null,
    assessmentType: data.assessmentType || 'cosmic',
    // When true, hide the "map your own, free" banner CTA. Used for coach-shared
    // recovery pages (e.g. a client's result the coach sends privately) where the
    // viral invite is not wanted.
    hideCta: !!data.hideCta,
  };
}
